import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYT3dPolyline, FikoYT3dPolylineHandles, IPolylineAyarlari, PolylineStili, PolylineTipi } from './fikoyt-3d-polyline';
import { FikoSahne } from '../../FikoSahne';
import { FikoYTService } from '@/src/service/fikoyt.service';

/**
 * Hazır şekil tipi
 */
export type HazirSekilTipi = 'serbest' | 'ucgen' | 'kare' | 'besgen' | 'altigen' | 'daire' | 'yildiz' | 'dikdortgen' | 'cokgen';

/**
 * FikoYT 3D Polyline Dialog Component
 *
 * Polyline (çoklu çizgi) oluşturma ve düzenleme dialog'u.
 * Noktalar eklenebilir, silinebilir ve sürüklenerek düzenlenebilir.
 */
@Component({
    selector: 'fikoyt-3d-polyline-dialog',
    templateUrl: './fikoyt-3d-polyline-dialog.component.html',
    styleUrls: ['./fikoyt-3d-polyline-dialog.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class FikoYT3dPolylineDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    // ========================================================================
    // INPUT/OUTPUT
    // ========================================================================

    /** FikoDialogService pencere referansı */
    private _pencere?: any;
    get pencere(): any { return this._pencere; }
    @Input()
    set pencere(v: any) {
        const yeniPencere = v?.instance || v;
        const eskiPencere = this._pencere;
        this._pencere = yeniPencere;

        if (yeniPencere && eskiPencere) {
            if (this._duzenleModu && this._mevcutPolyline) {
                this.dialogYenidenAcildi();
            } else if (!this._duzenleModu) {
                this.yeniPolylineModuBaslat();
            }
        }
    }

    /** Ana gösterim referansı */
    @Input() anaGosterim?: any;

    /** Sahne referansı */
    @Input() sahne?: FikoSahne;

    /** Düzenleme modunda mı */
    private _duzenleModu: boolean = false;
    @Input()
    get duzenleModu(): boolean { return this._duzenleModu; }
    set duzenleModu(v: boolean) {
        const eskiDeger = this._duzenleModu;
        this._duzenleModu = v;
        if (v && !eskiDeger && this._mevcutPolyline) {
            this.mevcutPolylineyiYukle();
            this.handleDurdur();
            this.handleBaslat();
        }
    }

    /** Düzenlenecek polyline */
    private _mevcutPolyline?: FikoYT3dPolyline;
    @Input()
    get mevcutPolyline(): FikoYT3dPolyline | undefined { return this._mevcutPolyline; }
    set mevcutPolyline(v: FikoYT3dPolyline | undefined) {
        this._mevcutPolyline = v;
        if (v && this._duzenleModu) {
            this.mevcutPolylineyiYukle();
            this.handleDurdur();
            this.handleBaslat();
        }
    }

    /** Dialog görünürlüğü */
    @Input() gorunur: boolean = false;

    /** Polyline oluşturulduğunda */
    @Output() polylineOlusturuldu = new EventEmitter<FikoYT3dPolyline>();

    /** Dialog kapandığında */
    @Output() kapandi = new EventEmitter<void>();

    // ========================================================================
    // FORM DEĞERLERİ
    // ========================================================================

    // Temel
    isim: string = '';
    kapali: boolean = false;

    // Noktalar listesi (düzenlenebilir)
    noktalar: { x: number; y: number; z: number }[] = [
        { x: 0, y: 0, z: 0 },
        { x: 50, y: 0, z: 0 },
        { x: 25, y: 50, z: 0 }
    ];

    // Seçili nokta indexi
    seciliNoktaIndex: number = -1;

    // Hazır şekil ayarları
    hazirSekil: HazirSekilTipi = 'serbest';
    sekilMerkezX: number = 0;
    sekilMerkezY: number = 0;
    sekilMerkezZ: number = 0;
    sekilYaricap: number = 50;
    sekilKenarSayisi: number = 6;
    sekilGenislik: number = 100;
    sekilYukseklik: number = 50;
    yildizIcYaricap: number = 25;
    yildizKolSayisi: number = 5;
    cokgenKenarSayisi: number = 7;
    daireNoktaSayisi: number = 36;

    // Tolerans bazlı otomatik nokta sayısı
    tolerans: number = 0.01; // mm (işleme toleransı)
    otomatikNoktaSayisi: boolean = true;

    // Görünüm ayarları
    renk: string = '#00ffff';
    kalinlik: number = 2;
    opaklik: number = 1;
    stil: PolylineStili = 'duz';
    kesikliAralik: number = 5;
    kesikliBoyut: number = 10;

    // Diğer ayarlar
    noktaGoster: boolean = true;
    noktaBoyutu: number = 4;
    noktaRengi: string = '#ffff00';
    enUstte: boolean = false;
    renderEt: boolean = true;
    secilebilir: boolean = true;
    katman: number = 0;

    // Stil seçenekleri
    stilSecenekleri = [
        { deger: 'duz', etiket: 'Düz' },
        { deger: 'kesikli', etiket: 'Kesikli' },
        { deger: 'noktali', etiket: 'Noktalı' },
        { deger: 'kesikli-noktali', etiket: 'Kesikli-Noktalı' }
    ];

    // Çizgi tipi
    cizgiTipi: PolylineTipi = 'Line2';
    cizgiTipiSecenekleri = [
        { deger: 'Line2', etiket: 'Line2 (Kalın)' },
        { deger: 'Line', etiket: 'Line (İnce)' },
        { deger: 'LineSegments', etiket: 'LineSegments' }
    ];

    // Hazır şekil seçenekleri
    sekilSecenekleri = [
        { deger: 'serbest', etiket: 'Serbest Çizim' },
        { deger: 'ucgen', etiket: 'Üçgen' },
        { deger: 'kare', etiket: 'Kare' },
        { deger: 'besgen', etiket: 'Beşgen' },
        { deger: 'altigen', etiket: 'Altıgen' },
        { deger: 'cokgen', etiket: 'Çokgen (N)' },
        { deger: 'daire', etiket: 'Daire (yaklaşık)' },
        { deger: 'yildiz', etiket: 'Yıldız' },
        { deger: 'dikdortgen', etiket: 'Dikdörtgen' }
    ];

    // Aktif tab
    aktifTab: 'noktalar' | 'sekil' | 'stil' | 'diger' = 'noktalar';

    // ========================================================================
    // BODY SEÇİMİ (CATIA-style)
    // ========================================================================

    /** Seçili aktif body */
    seciliAktifBody: any = null;

    /** Aktif body listesi (ürün ağacından) */
    aktifBodyListesi: { id: string; isim: string; nesne: any }[] = [];

    /** Body'ye ekleme aktif mi */
    aktifBodyeEkle: boolean = true;

    /** Body seçme modu aktif mi */
    bodySecModu: boolean = false;

    /** Body seçimi için subscription */
    private bodySecimSubscription?: Subscription;

    /** Seçili body'nin görüntülenen ismi */
    get seciliAktifBodyIsim(): string {
        if (!this.seciliAktifBody) return '(Seçilmedi)';
        return this.seciliAktifBody.name || this.seciliAktifBody.isim || 'Body';
    }

    // ========================================================================
    // DÜZLEM SİSTEMİ (Matematiksel: ax + by + cz + d = 0)
    // ========================================================================

    /** Düzlem normal vektörü (i, j, k bileşenleri) */
    duzlemNormal: { x: number; y: number; z: number } = { x: 0, y: 0, z: 1 }; // Varsayılan: XY düzlemi (z yönü)

    /** Düzlem kamera takip modu (true = kamera yönüne göre, false = sabit düzlem) */
    duzlemKameraTakip: boolean = true;

    /** Şekil oluşturma sırasında UI güncellemesi yapılıyor (sonsuz döngü önleme) */
    private _sekilOlusturuluyor: boolean = false;

    /** Düzlem sabiti (orijinden uzaklık) */
    duzlemD: number = 0;

    /** THREE.Plane nesnesi (hesaplanmış) */
    private _duzlemPlane: THREE.Plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

    /** Düzlem ayarları paneli görünür mü */
    duzlemAyarGoster: boolean = false;

    /** Düzlem seçme modu (3D'den yüzey/düzlem seçimi) */
    duzlemSecModu: boolean = false;

    /** Seçili yüzey/düzlem nesnesi */
    seciliYuzey: THREE.Object3D | null = null;

    /** Seçili yüzeyin gösterim ismi */
    seciliYuzeyIsmi: string = '';

    /** Düzlem seçme click handler */
    private duzlemSecClickHandler?: (event: MouseEvent) => void;

    /** Düzlem seçme context menu handler (sağ tık) */
    private duzlemSecContextMenuHandler?: (event: MouseEvent) => void;

    // ========================================================================
    // KOORDİNAT DÜZENLEME DİALOGU
    // ========================================================================

    /** Koordinat dialog açık mı */
    koordinatDialogAcik: boolean = false;

    /** Düzenlenen nokta indexi */
    koordinatDialogIndex: number | null = null;

    // ========================================================================
    // ARDIŞIK TIKLAMA MODU
    // ========================================================================

    /** Ardışık tıklama modu (her tıklamada yeni nokta ekle) */
    ardisikTiklamaModu: boolean = false;

    /** Ardışık tıklama için click handler */
    private ardisikTiklamaHandler?: (event: MouseEvent) => void;

    /** Ardışık tıklama için contextmenu handler (sağ tık ile bitirmek için) */
    private ardisikContextMenuHandler?: (event: MouseEvent) => void;

    // ========================================================================
    // NOKTA SEÇME MODU (Mouse ile 3D'den seçim)
    // ========================================================================

    /** Nokta seçme modu (null = kapalı, sayı = hangi nokta indexi seçiliyor) */
    noktaSecModu: number | null = null;

    /** Nokta seçme click handler */
    private noktaSecClickHandler?: (event: MouseEvent) => void;

    // ========================================================================
    // PRIVATE MEMBERS
    // ========================================================================

    /** Geçici polyline (oluşturma sırasında) */
    private geciciPolyline?: FikoYT3dPolyline;
    private geciciPolylineAktif: boolean = false;

    /** Handle sistemi */
    private handles?: FikoYT3dPolylineHandles;
    private handleAktif: boolean = false;
    private handleRaycaster: THREE.Raycaster = new THREE.Raycaster();
    private dragPlane: THREE.Plane = new THREE.Plane();
    private dragOffset: THREE.Vector3 = new THREE.Vector3();

    /** Event listener referansları */
    private handleMouseDownRef?: (e: MouseEvent) => void;
    private handleMouseMoveRef?: (e: MouseEvent) => void;
    private handleMouseUpRef?: (e: MouseEvent) => void;

    /** Handle retry mekanizması (Line dialog pattern) */
    private handleBaslatRetryCount: number = 0;
    private readonly HANDLE_MAX_RETRY = 5;
    private readonly HANDLE_RETRY_DELAY = 100; // ms
    private handleBaslatTimeoutId?: ReturnType<typeof setTimeout>;

    private fikoYT = inject(FikoYTService);
    private ngZone = inject(NgZone);

    constructor(private cd: ChangeDetectorRef) {}

    // ========================================================================
    // LIFECYCLE
    // ========================================================================

    ngOnInit(): void {
        // Sahne referansını pencereden al (varsa)
        if (this._pencere?.bilesenParametreler) {
            const params = this._pencere.bilesenParametreler;
            if (params.sahne) this.sahne = params.sahne;
            if (params.anaGosterim) this.anaGosterim = params.anaGosterim;
            if (params.mevcutPolyline) {
                this._mevcutPolyline = params.mevcutPolyline;
                this._duzenleModu = true;
            }
            if (params.duzenleModu !== undefined) this._duzenleModu = params.duzenleModu;
        }

        // Sahne referansını ayarla (fallback)
        if (!this.sahne && this.anaGosterim?.sahneler?.[0]) {
            this.sahne = this.anaGosterim.sahneler[0];
        }

        // Global dialog tracking'e kayıt ol (duplicate dialog önleme)
        FikoYT3dPolyline.globalDialogAyarla({ gorunur: true, pencere: this._pencere });

        // Aktif body listesini yükle (ürün ağacından)
        this.aktifBodyListesiniYukle();

        // Düzenleme modunda değilse geçici polyline oluştur
        if (!this._duzenleModu) {
            setTimeout(() => this.geciciPolylineOlustur(), 100);
        } else if (this._mevcutPolyline) {
            this.mevcutPolylineyiYukle();
        }
    }

    ngAfterViewInit(): void {
        // Handle'ları başlat
        setTimeout(() => {
            if (this._duzenleModu && this._mevcutPolyline) {
                this.handleBaslat();
            } else if (this.geciciPolyline) {
                this.handleBaslat();
            }
        }, 200);
    }

    ngOnDestroy(): void {
        // Global dialog tracking'den çıkart
        FikoYT3dPolyline.globalDialogTemizle();

        // Ardışık tıklama modunu durdur
        this.ardisikTiklamaDurdur();

        // Nokta seçme modunu durdur
        this.noktaSecDurdur();

        // Body seçme modunu durdur
        this.bodySecDurdur();

        // Düzlem seçme modunu durdur
        this.duzlemSecDurdur();

        // Handle'ları durdur ve temizle
        this.handleDurdur();

        // Geçici polyline'ı sil
        this.geciciPolylineSil();

        // Orphan handle'ları da temizle (ekstra güvenlik)
        this.tumOrphanHandleTemizle();
    }

    /**
     * Tüm orphan handle'ları ve geçici polyline'ları temizle
     */
    private tumOrphanHandleTemizle(): void {
        const sahne = this.sahne || this.anaGosterim?.sahneler?.[0];
        if (!sahne) return;

        const silinecekler: THREE.Object3D[] = [];
        sahne.traverse((child: THREE.Object3D) => {
            // PolylineHandles_ ile başlayan grup'ları bul
            if (child.name && child.name.startsWith('PolylineHandles_')) {
                silinecekler.push(child);
            }
            // PolylineHandle_ ile başlayan mesh'leri bul
            if (child.name && child.name.startsWith('PolylineHandle_')) {
                silinecekler.push(child);
            }
            // Geçici_Polyline isimli nesneleri temizle
            if (child.name === 'Geçici_Polyline') {
                silinecekler.push(child);
            }
        });

        silinecekler.forEach(obj => {
            if (obj.parent) {
                obj.parent.remove(obj);
            }
            // Geometri ve materyalleri dispose et
            obj.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    if (mesh.geometry) mesh.geometry.dispose();
                    if (mesh.material) {
                        if (Array.isArray(mesh.material)) {
                            mesh.material.forEach(m => m.dispose());
                        } else {
                            mesh.material.dispose();
                        }
                    }
                }
                // Line2 için de temizle
                if ((child as any).isLine2) {
                    const line = child as any;
                    if (line.geometry) line.geometry.dispose();
                    if (line.material) line.material.dispose();
                }
            });
        });
    }

    // ========================================================================
    // DIALOG YÖNETİMİ
    // ========================================================================

    /**
     * Dialog'u aç
     */
    ac(): void {
        this.gorunur = true;

        if (!this._duzenleModu && !this.geciciPolyline) {
            this.geciciPolylineOlustur();
        }

        setTimeout(() => this.handleBaslat(), 100);
    }

    /**
     * Dialog'u kapat
     */
    kapat(): void {
        // Global dialog tracking'den çıkart
        FikoYT3dPolyline.globalDialogTemizle();

        // Ardışık tıklama modunu durdur
        this.ardisikTiklamaDurdurInternal();

        // Nokta seçme modunu durdur
        this.noktaSecDurdur();

        // Body seçme modunu durdur
        this.bodySecDurdur();

        // Düzlem seçme modunu durdur
        this.duzlemSecDurdur();

        // Handle'ları durdur
        this.handleDurdur();

        // Geçici polyline'ı sil
        this.geciciPolylineSil();

        // Orphan handle'ları temizle
        this.tumOrphanHandleTemizle();

        this.gorunur = false;

        if (this._pencere?.kapa) {
            this._pencere.kapa();
        }

        this.kapandi.emit();
    }

    /**
     * Dialog yeniden açıldığında
     */
    private dialogYenidenAcildi(): void {
        this.handleDurdur();

        if (this._mevcutPolyline) {
            this.mevcutPolylineyiYukle();
        }

        setTimeout(() => this.handleBaslat(), 100);
    }

    /**
     * Yeni polyline modu başlat
     */
    private yeniPolylineModuBaslat(): void {
        this.handleDurdur();
        this.geciciPolylineSil();
        this.formDegerleriniSifirla();

        setTimeout(() => {
            this.geciciPolylineOlustur();
        }, 50);
    }

    /**
     * Form değerlerini sıfırla
     */
    private formDegerleriniSifirla(): void {
        this.isim = '';
        this.kapali = false;
        this.noktalar = [
            { x: 0, y: 0, z: 0 },
            { x: 50, y: 0, z: 0 },
            { x: 25, y: 50, z: 0 }
        ];
        this.seciliNoktaIndex = -1;
        this.hazirSekil = 'serbest';
        this.renk = '#00ffff';
        this.kalinlik = 2;
        this.opaklik = 1;
        this.stil = 'duz';
        this.noktaGoster = true;
        this.enUstte = false;
        this.renderEt = true;
        this.secilebilir = true;
        this.katman = 0;
    }

    // ========================================================================
    // GEÇİCİ POLYLINE
    // ========================================================================

    /**
     * Geçici polyline oluştur
     */
    private geciciPolylineOlustur(): void {
        // Sahne referansını al (fallback: anaGosterim.sahneler[0])
        const hedefSahne = this.sahne || this.anaGosterim?.sahneler?.[0];
        if (!hedefSahne) {
            console.warn('[FikoYT3dPolyline] Sahne bulunamadı, geçici polyline oluşturulamıyor');
            return;
        }

        // Sahne referansını kaydet
        this.sahne = hedefSahne;

        // Mevcut geçiciyi temizle
        this.geciciPolylineSil();

        // Yeni polyline
        this.geciciPolyline = new FikoYT3dPolyline(hedefSahne, 'Geçici_Polyline');

        // Noktaları ayarla
        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        this.geciciPolyline.noktalarileBelirle(noktalarVec, this.kapali);

        // Ayarları uygula
        this.ayarlariUygula(this.geciciPolyline);

        // Sahneye ekle
        this.sahne.add(this.geciciPolyline);
        this.geciciPolylineAktif = true;

        // Materyali tazele (resolution güncelle) - ilk render için kritik
        setTimeout(() => {
            if (this.geciciPolyline) {
                this.geciciPolyline.materyaliTazele();
            }
        }, 10);

        // Handle'ları başlat
        setTimeout(() => {
            if (this.geciciPolyline) {
                this.handles = this.geciciPolyline.handlelerGoster();
                this.handleBaslat();
            }
        }, 50);
    }

    /**
     * Geçici polyline sil
     */
    private geciciPolylineSil(): void {
        if (this.geciciPolyline) {
            this.geciciPolyline.handlelerGizle();
            this.geciciPolyline.sil();
            this.geciciPolyline = undefined;
            this.geciciPolylineAktif = false;
        }
    }

    /**
     * Geçici polyline'ı güncelle
     */
    private geciciPolylineGuncelle(): void {
        const hedefPolyline = this.geciciPolylineAktif ? this.geciciPolyline : this._mevcutPolyline;
        if (!hedefPolyline) return;

        // Noktaları güncelle
        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        hedefPolyline.noktalarileBelirle(noktalarVec, this.kapali);

        // Ayarları uygula
        this.ayarlariUygula(hedefPolyline);

        // Materyali tazele (resolution güncelle)
        hedefPolyline.materyaliTazele();

        // Handle'ları güncelle
        if (this.handles) {
            this.handles.guncelle();
        }
    }

    // ========================================================================
    // MEVCUT POLYLINE DÜZENLEME
    // ========================================================================

    /**
     * Mevcut polyline'ı form'a yükle
     */
    private mevcutPolylineyiYukle(): void {
        if (!this._mevcutPolyline) return;

        this.isim = this._mevcutPolyline.name;
        this.kapali = this._mevcutPolyline.kapali;

        // Noktaları yükle
        this.noktalar = this._mevcutPolyline.noktalar.map(n => ({
            x: Math.round(n.x * 100) / 100,
            y: Math.round(n.y * 100) / 100,
            z: Math.round(n.z * 100) / 100
        }));

        // Görünüm ayarları
        this.cizgiTipi = this._mevcutPolyline.cizgiTipi;
        const renkHex = typeof this._mevcutPolyline.renk === 'number'
            ? '#' + this._mevcutPolyline.renk.toString(16).padStart(6, '0')
            : this._mevcutPolyline.renk as string;
        this.renk = renkHex;
        this.kalinlik = this._mevcutPolyline.kalinlik;
        this.opaklik = this._mevcutPolyline.opaklık;
        this.stil = this._mevcutPolyline.stil;
        this.noktaGoster = this._mevcutPolyline.noktaGoster;
        this.enUstte = this._mevcutPolyline.enUstte;
        this.renderEt = this._mevcutPolyline.renderEt;
        this.secilebilir = this._mevcutPolyline.secilebilir;

        this.cd.detectChanges();
    }

    // ========================================================================
    // AYAR UYGULAMA
    // ========================================================================

    /**
     * Ayarları polyline'a uygula
     */
    private ayarlariUygula(polyline: FikoYT3dPolyline): void {
        polyline.cizgiTipi = this.cizgiTipi;
        polyline.renk = this.renk;
        polyline.kalinlik = this.kalinlik;
        polyline.opaklık = this.opaklik;
        polyline.stil = this.stil;
        polyline.noktaGoster = this.noktaGoster;
        polyline.enUstte = this.enUstte;
        polyline.renderEt = this.renderEt;
        polyline.secilebilir = this.secilebilir;
        polyline.kapali = this.kapali;
    }

    // ========================================================================
    // NOKTA YÖNETİMİ
    // ========================================================================

    /**
     * Yeni nokta ekle
     */
    noktaEkle(): void {
        // Son noktadan biraz ileriye ekle
        const sonNokta = this.noktalar[this.noktalar.length - 1];
        let yeniNoktaVec = new THREE.Vector3(sonNokta.x + 25, sonNokta.y, sonNokta.z);

        // Seçili düzlem varsa, yeni noktayı düzleme project et
        yeniNoktaVec = this.noktayiDuzlemeProject(yeniNoktaVec);

        const yeniNokta = {
            x: Math.round(yeniNoktaVec.x * 100) / 100,
            y: Math.round(yeniNoktaVec.y * 100) / 100,
            z: Math.round(yeniNoktaVec.z * 100) / 100
        };
        this.noktalar.push(yeniNokta);
        this.seciliNoktaIndex = this.noktalar.length - 1;
        this.geciciPolylineGuncelle();
    }

    /**
     * İki nokta arasına nokta ekle
     */
    arayaNoktaEkle(index: number): void {
        if (index < 0 || index >= this.noktalar.length - 1) return;

        const p1 = this.noktalar[index];
        const p2 = this.noktalar[index + 1];
        let ortaVec = new THREE.Vector3(
            (p1.x + p2.x) / 2,
            (p1.y + p2.y) / 2,
            (p1.z + p2.z) / 2
        );

        // Seçili düzlem varsa, orta noktayı düzleme project et
        ortaVec = this.noktayiDuzlemeProject(ortaVec);

        const orta = {
            x: Math.round(ortaVec.x * 100) / 100,
            y: Math.round(ortaVec.y * 100) / 100,
            z: Math.round(ortaVec.z * 100) / 100
        };
        this.noktalar.splice(index + 1, 0, orta);
        this.seciliNoktaIndex = index + 1;
        this.geciciPolylineGuncelle();
    }

    /**
     * Nokta sil
     */
    noktaSil(index: number): void {
        if (this.noktalar.length <= 2) {
            alert('En az 2 nokta olmalı!');
            return;
        }
        this.noktalar.splice(index, 1);
        if (this.seciliNoktaIndex >= this.noktalar.length) {
            this.seciliNoktaIndex = this.noktalar.length - 1;
        }
        this.geciciPolylineGuncelle();
    }

    /**
     * Nokta seç
     */
    noktaSec(index: number): void {
        this.seciliNoktaIndex = index;
    }

    /**
     * Nokta değiştiğinde
     */
    noktaDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    /**
     * Koordinat düzenleme dialogunu aç
     */
    noktaDuzenleDialogAc(index: number): void {
        this.koordinatDialogIndex = index;
        this.koordinatDialogAcik = true;
        this.seciliNoktaIndex = index;
        this.cd.detectChanges();
    }

    /**
     * Koordinat düzenleme dialogunu kapat
     */
    koordinatDialogKapat(): void {
        this.koordinatDialogAcik = false;
        this.koordinatDialogIndex = null;
        this.cd.detectChanges();
    }

    // ========================================================================
    // HAZIR ŞEKİLLER
    // ========================================================================

    /**
     * Hazır şekil değiştiğinde
     */
    hazirSekilDegisti(): void {
        // Daire seçildiğinde ve otomatik modda ise nokta sayısını hesapla
        if (this.otomatikNoktaSayisi && this.hazirSekil === 'daire') {
            this.daireNoktaSayisi = this.toleransIleNoktaSayisiHesapla(this.sekilYaricap, this.tolerans);
        }
        this.hazirSekilOlustur();
    }

    /**
     * Tolerans bazlı optimal nokta sayısı hesapla
     * Formül: n = π / arccos(1 - tolerans/yarıçap)
     * Bu formül, dairedeki maksimum sapmanın toleransı geçmemesini sağlar
     */
    toleransIleNoktaSayisiHesapla(yaricap: number, tolerans: number): number {
        if (yaricap <= 0 || tolerans <= 0) return 36;

        // Tolerans yarıçaptan büyükse minimum nokta sayısı
        if (tolerans >= yaricap) return 6;

        // n = π / arccos(1 - T/R)
        const oran = tolerans / yaricap;
        const aci = Math.acos(1 - oran);
        let noktaSayisi = Math.ceil(Math.PI / aci);

        // Minimum 6, maksimum 10000 (performans için)
        noktaSayisi = Math.max(6, Math.min(10000, noktaSayisi));

        return noktaSayisi;
    }

    /**
     * Tolerans değiştiğinde
     */
    toleransDegisti(): void {
        if (this.otomatikNoktaSayisi && (this.hazirSekil === 'daire' || this.hazirSekil === 'cokgen')) {
            this.daireNoktaSayisi = this.toleransIleNoktaSayisiHesapla(this.sekilYaricap, this.tolerans);
        }
        this.hazirSekilOlustur();
    }

    /**
     * Otomatik mod değiştiğinde
     */
    otomatikModDegisti(): void {
        if (this.otomatikNoktaSayisi) {
            this.daireNoktaSayisi = this.toleransIleNoktaSayisiHesapla(this.sekilYaricap, this.tolerans);
        }
        this.hazirSekilOlustur();
    }

    /**
     * Hazır şekil oluştur
     */
    hazirSekilOlustur(): void {
        // Sonsuz döngüyü önlemek için flag
        this._sekilOlusturuluyor = true;

        try {
            let merkez = new THREE.Vector3(this.sekilMerkezX, this.sekilMerkezY, this.sekilMerkezZ);

            // Sabit düzlem modunda merkezi düzleme project et
            if (!this.duzlemKameraTakip) {
                merkez = this.noktayiDuzlemeProject(merkez);
                // UI'daki değerleri de güncelle (project edilmiş merkez)
                this.sekilMerkezX = Math.round(merkez.x * 100) / 100;
                this.sekilMerkezY = Math.round(merkez.y * 100) / 100;
                this.sekilMerkezZ = Math.round(merkez.z * 100) / 100;
            }

            switch (this.hazirSekil) {
            case 'ucgen':
                this.duzenliCokgenNoktalar(merkez, this.sekilYaricap, 3);
                this.kapali = true;
                break;
            case 'kare':
                this.duzenliCokgenNoktalar(merkez, this.sekilYaricap, 4);
                this.kapali = true;
                break;
            case 'besgen':
                this.duzenliCokgenNoktalar(merkez, this.sekilYaricap, 5);
                this.kapali = true;
                break;
            case 'altigen':
                this.duzenliCokgenNoktalar(merkez, this.sekilYaricap, 6);
                this.kapali = true;
                break;
            case 'cokgen':
                this.duzenliCokgenNoktalar(merkez, this.sekilYaricap, this.cokgenKenarSayisi);
                this.kapali = true;
                break;
            case 'daire':
                this.duzenliCokgenNoktalar(merkez, this.sekilYaricap, this.daireNoktaSayisi);
                this.kapali = true;
                break;
            case 'yildiz':
                this.yildizNoktalar(merkez, this.sekilYaricap, this.yildizIcYaricap, this.yildizKolSayisi);
                this.kapali = true;
                break;
            case 'dikdortgen':
                this.dikdortgenNoktalar(merkez, this.sekilGenislik, this.sekilYukseklik);
                this.kapali = true;
                break;
            default:
                // Serbest - mevcut noktaları koru
                break;
            }

            this.geciciPolylineGuncelle();
        } finally {
            // Flag'i her durumda sıfırla
            this._sekilOlusturuluyor = false;
        }
    }

    /**
     * Düzlem üzerinde yerel U ve V eksenlerini hesapla
     * Normal vektörüne dik iki eksen döndürür
     */
    private duzlemYerelEksenler(): { u: THREE.Vector3; v: THREE.Vector3; normal: THREE.Vector3 } {
        let normal: THREE.Vector3;

        if (this.duzlemKameraTakip) {
            // Kamera takip modu - kamera yönüne dik düzlem
            const anaGosterim = this.anaGosterim;
            if (anaGosterim?.aktifKamera) {
                normal = new THREE.Vector3();
                anaGosterim.aktifKamera.getWorldDirection(normal);
                normal.negate(); // Kameraya bakan normal
            } else {
                normal = new THREE.Vector3(0, 0, 1); // Varsayılan
            }
        } else {
            // Sabit düzlem modu
            normal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
            if (normal.length() < 0.0001) {
                normal.set(0, 0, 1);
            }
            normal.normalize();
        }

        // U ekseni: Normal ile yukarı vektörün çapraz çarpımı
        let up = new THREE.Vector3(0, 1, 0);
        // Eğer normal yukarıya çok yakınsa, farklı bir referans kullan
        if (Math.abs(normal.dot(up)) > 0.99) {
            up = new THREE.Vector3(1, 0, 0);
        }

        const u = new THREE.Vector3().crossVectors(up, normal).normalize();
        const v = new THREE.Vector3().crossVectors(normal, u).normalize();

        return { u, v, normal };
    }

    /**
     * 2D koordinatı (yerel U-V düzleminde) 3D'ye dönüştür
     */
    private yerelKoordinat3D(merkez: THREE.Vector3, localX: number, localY: number, u: THREE.Vector3, v: THREE.Vector3): { x: number; y: number; z: number } {
        const point = new THREE.Vector3()
            .addScaledVector(u, localX)
            .addScaledVector(v, localY)
            .add(merkez);

        return {
            x: Math.round(point.x * 100) / 100,
            y: Math.round(point.y * 100) / 100,
            z: Math.round(point.z * 100) / 100
        };
    }

    /**
     * Düzenli çokgen noktaları (düzleme göre)
     */
    private duzenliCokgenNoktalar(merkez: THREE.Vector3, yaricap: number, kenarSayisi: number): void {
        this.noktalar = [];
        const aciAdim = (Math.PI * 2) / kenarSayisi;
        const { u, v } = this.duzlemYerelEksenler();

        for (let i = 0; i < kenarSayisi; i++) {
            const aci = aciAdim * i - Math.PI / 2;
            const localX = Math.cos(aci) * yaricap;
            const localY = Math.sin(aci) * yaricap;
            this.noktalar.push(this.yerelKoordinat3D(merkez, localX, localY, u, v));
        }
    }

    /**
     * Yıldız noktaları (düzleme göre)
     */
    private yildizNoktalar(merkez: THREE.Vector3, disYaricap: number, icYaricap: number, kolSayisi: number): void {
        this.noktalar = [];
        const noktaSayisi = kolSayisi * 2;
        const aciAdim = (Math.PI * 2) / noktaSayisi;
        const { u, v } = this.duzlemYerelEksenler();

        for (let i = 0; i < noktaSayisi; i++) {
            const aci = aciAdim * i - Math.PI / 2;
            const yaricap = i % 2 === 0 ? disYaricap : icYaricap;
            const localX = Math.cos(aci) * yaricap;
            const localY = Math.sin(aci) * yaricap;
            this.noktalar.push(this.yerelKoordinat3D(merkez, localX, localY, u, v));
        }
    }

    /**
     * Dikdörtgen noktaları (düzleme göre)
     */
    private dikdortgenNoktalar(merkez: THREE.Vector3, genislik: number, yukseklik: number): void {
        const yarimG = genislik / 2;
        const yarimY = yukseklik / 2;
        const { u, v } = this.duzlemYerelEksenler();

        this.noktalar = [
            this.yerelKoordinat3D(merkez, -yarimG, -yarimY, u, v),
            this.yerelKoordinat3D(merkez, +yarimG, -yarimY, u, v),
            this.yerelKoordinat3D(merkez, +yarimG, +yarimY, u, v),
            this.yerelKoordinat3D(merkez, -yarimG, +yarimY, u, v)
        ];
    }

    // ========================================================================
    // BODY SEÇİM SİSTEMİ (CATIA-style)
    // ========================================================================

    /**
     * Body seçme modunu başlat
     */
    bodySecBaslat(): void {
        // Zaten seçim modundaysa kapat
        if (this.bodySecModu) {
            this.bodySecDurdur();
            return;
        }

        // Nokta seçme modunu kapat
        if (this.noktaSecModu !== null) {
            this.noktaSecDurdur();
        }

        this.bodySecModu = true;

        // FikoYT.degisti event'ini dinle
        this.bodySecimSubscription = this.fikoYT.degisti.subscribe(() => {
            if (this.bodySecModu) {
                const aktifItem = this.anaGosterim?.aktifItem;
                if (aktifItem) {
                    const body = this.uygunBodyBul(aktifItem);
                    this.seciliAktifBody = body || aktifItem;
                    this.bodySecDurdur();
                }
            }
        });

        this.cd.detectChanges();
    }

    /**
     * Body seçme modunu durdur
     */
    bodySecDurdur(): void {
        this.bodySecModu = false;

        if (this.bodySecimSubscription) {
            this.bodySecimSubscription.unsubscribe();
            this.bodySecimSubscription = undefined;
        }

        this.cd.detectChanges();
    }

    /**
     * Uygun body bul
     */
    private uygunBodyBul(nesne: any): any {
        if (!nesne) return null;

        // Body, Part veya PartBody ise doğrudan kullan
        const tip = nesne.type || nesne.constructor?.name || '';
        if (tip.includes('Body') || tip.includes('Part') || tip.includes('Cizim')) {
            return nesne;
        }

        // Parent'a bak
        if (nesne.parent && nesne.parent !== nesne) {
            return this.uygunBodyBul(nesne.parent);
        }

        return nesne;
    }

    /**
     * Ürün ağacından mevcut body'leri yükle
     */
    private aktifBodyListesiniYukle(): void {
        this.aktifBodyListesi = [];

        // anaGosterim üzerinden ürün ağacına eriş
        const urunAgaci = this.anaGosterim?.sahneler?.[0]?.urunAgaci;
        if (!urunAgaci) {
            // Ürün ağacı yoksa, sahneyi kullan
            if (this.sahne) {
                this.aktifBodyListesi.push({
                    id: 'sahne',
                    isim: 'Ana Sahne',
                    nesne: this.sahne
                });
                this.seciliAktifBody = this.sahne;
            }
            return;
        }

        // Ürün ağacında body'leri bul (recursive)
        this.bodyleriBul(urunAgaci);

        // Varsayılan olarak ilk body'yi seç
        if (this.aktifBodyListesi.length > 0) {
            this.seciliAktifBody = this.aktifBodyListesi[0].nesne;
        }
    }

    /**
     * Ürün ağacında recursive olarak body'leri bul
     */
    private bodyleriBul(grup: any, derinlik: number = 0): void {
        if (!grup) return;

        // Bu grup bir body mi kontrol et
        const isBody = grup.tipi === 'Body' ||
                       grup.tipi === 'Part' ||
                       grup.tipi === 'PartBody' ||
                       grup.type?.includes('Body') ||
                       grup.name?.toLowerCase().includes('body') ||
                       grup.isim?.toLowerCase().includes('body');

        if (isBody || derinlik === 0) {
            const indent = '  '.repeat(derinlik);
            this.aktifBodyListesi.push({
                id: grup.id || grup.uuid || String(this.aktifBodyListesi.length),
                isim: indent + (grup.isim || grup.name || `Body ${this.aktifBodyListesi.length + 1}`),
                nesne: grup
            });
        }

        // Alt grupları tara
        const altlar = grup._liste || grup.liste || grup.children || [];
        if (Array.isArray(altlar)) {
            for (const alt of altlar) {
                if (alt && typeof alt === 'object') {
                    this.bodyleriBul(alt, derinlik + 1);
                }
            }
        }
    }

    // ========================================================================
    // NOKTA SEÇME SİSTEMİ (Mouse ile 3D'den seçim)
    // ========================================================================

    /**
     * Nokta seçme modunu başlat
     * @param index Düzenlenecek nokta indexi (-1 = yeni nokta ekle)
     */
    noktaSecBaslat(index: number): void {
        // Aynı moda tıklandıysa kapat
        if (this.noktaSecModu === index) {
            this.noktaSecDurdur();
            return;
        }

        // Body seçme modunu kapat
        if (this.bodySecModu) {
            this.bodySecDurdur();
        }

        this.noktaSecModu = index;

        // Canvas'ı bul
        const canvas = this.canvasBul();
        if (!canvas) {
            this.noktaSecModu = null;
            return;
        }

        // Önceki handler'ı temizle
        if (this.noktaSecClickHandler) {
            canvas.removeEventListener('click', this.noktaSecClickHandler);
        }

        // Yeni click handler
        this.noktaSecClickHandler = (event: MouseEvent) => {
            this.noktaSecCanvasTiklandi(event, canvas);
        };

        canvas.addEventListener('click', this.noktaSecClickHandler);
        this.cd.detectChanges();
    }

    /**
     * Nokta seçme modunu durdur
     */
    noktaSecDurdur(): void {
        this.noktaSecModu = null;

        const canvas = this.canvasBul();
        if (canvas && this.noktaSecClickHandler) {
            canvas.removeEventListener('click', this.noktaSecClickHandler);
            this.noktaSecClickHandler = undefined;
        }

        this.cd.detectChanges();
    }

    /**
     * Canvas tıklandığında nokta seç
     */
    private noktaSecCanvasTiklandi(event: MouseEvent, canvas: HTMLCanvasElement): void {
        if (this.noktaSecModu === null) return;

        // Raycast ile 3D koordinat hesapla
        let hitPoint = this.raycastNoktaHesapla(event, canvas);
        if (!hitPoint) {
            // Model üzerine tıklanmadıysa düzlemde hesapla
            const planePoint = this.duzlemNoktaHesapla(event, canvas);
            if (!planePoint) return;
            hitPoint = planePoint;
        } else {
            // Model üzerine tıklandıysa, seçili düzleme project et
            hitPoint = this.noktayiDuzlemeProject(hitPoint);
        }

        const roundedX = Math.round(hitPoint.x * 100) / 100;
        const roundedY = Math.round(hitPoint.y * 100) / 100;
        const roundedZ = Math.round(hitPoint.z * 100) / 100;

        // noktaSecModu >= noktalar.length ise yeni nokta ekle (HTML'de noktalar.length geçiliyor)
        if (this.noktaSecModu >= this.noktalar.length) {
            // Yeni nokta ekle
            this.noktalar.push({ x: roundedX, y: roundedY, z: roundedZ });
            this.seciliNoktaIndex = this.noktalar.length - 1;
        } else if (this.noktaSecModu >= 0 && this.noktaSecModu < this.noktalar.length) {
            // Mevcut noktayı güncelle
            this.noktalar[this.noktaSecModu] = { x: roundedX, y: roundedY, z: roundedZ };
            this.seciliNoktaIndex = this.noktaSecModu;
        }

        // Polyline'ı güncelle
        this.geciciPolylineGuncelle();

        // Seçme modunu kapat
        this.noktaSecDurdur();
    }

    /**
     * Raycast ile 3D nokta hesapla
     */
    private raycastNoktaHesapla(event: MouseEvent, canvas: HTMLCanvasElement): THREE.Vector3 | null {
        const anaGosterim = this.anaGosterim || this.sahne?.anaGosterim;
        if (!anaGosterim?.aktifKamera) return null;

        const rect = canvas.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, anaGosterim.aktifKamera);

        // Sahne mesh'lerini topla
        const meshler: THREE.Object3D[] = [];
        const sahne = this.sahne || anaGosterim.sahneler?.[0];
        if (sahne) {
            sahne.traverse((obj: THREE.Object3D) => {
                if ((obj as THREE.Mesh).isMesh) {
                    meshler.push(obj);
                }
            });
        }

        if (meshler.length > 0) {
            const kesisimler = raycaster.intersectObjects(meshler, true);
            if (kesisimler.length > 0) {
                return kesisimler[0].point.clone();
            }
        }

        return null;
    }

    /**
     * Seçili düzlemde nokta hesapla (matematiksel düzlem kullanır)
     */
    private duzlemNoktaHesapla(event: MouseEvent, canvas: HTMLCanvasElement): THREE.Vector3 | null {
        const anaGosterim = this.anaGosterim || this.sahne?.anaGosterim;
        if (!anaGosterim?.aktifKamera) return null;

        const rect = canvas.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, anaGosterim.aktifKamera);

        let plane: THREE.Plane;

        if (this.duzlemKameraTakip) {
            // Kamera takip modu: kamera yönüne dik düzlem (kameranın baktığı noktada)
            const cameraDir = new THREE.Vector3();
            anaGosterim.aktifKamera.getWorldDirection(cameraDir);

            // Düzlem, kameraya bakan yüzey (normal = -cameraDir)
            // Düzlem noktası: kamera hedefi veya son nokta veya orijin
            let planePoint = new THREE.Vector3(0, 0, 0);
            if (this.noktalar.length > 0) {
                const sonNokta = this.noktalar[this.noktalar.length - 1];
                planePoint.set(sonNokta.x, sonNokta.y, sonNokta.z);
            }

            plane = new THREE.Plane();
            plane.setFromNormalAndCoplanarPoint(cameraDir.clone().negate(), planePoint);
        } else {
            // Sabit düzlem modu: manuel ayarlanmış veya yüzeyden seçilmiş düzlem
            const normal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
            if (normal.length() < 0.0001) {
                // Normal geçersizse varsayılan XY düzlemi kullan
                normal.set(0, 0, 1);
            } else {
                normal.normalize();
            }

            // THREE.Plane: constant = -d (THREE.js konvansiyonu)
            // Bizim denklemimiz: nx*x + ny*y + nz*z + d = 0
            // THREE.js denklemi: n·p + constant = 0 => constant = d
            plane = new THREE.Plane(normal, this.duzlemD);
        }

        const target = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(plane, target)) {
            return target;
        }

        return null;
    }

    // ========================================================================
    // DÜZLEM SEÇME SİSTEMİ (Matematiksel)
    // ========================================================================

    /**
     * Düzlem parametrelerini güncelle (normal ve d değerlerinden)
     * @param projectNoktalar true ise mevcut noktaları yeni düzleme project eder
     */
    duzlemGuncelle(projectNoktalar: boolean = true): void {
        // Manuel düzlem ayarlandı, kamera takip modunu kapat
        this.duzlemKameraTakip = false;

        // THREE.Plane nesnesini güncelle
        const normal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
        if (normal.length() > 0.0001) {
            normal.normalize();
        }
        this._duzlemPlane.set(normal, this.duzlemD);

        // Mevcut noktaları yeni düzleme project et
        if (projectNoktalar && this.noktalar.length > 0) {
            this.noktalariDuzlemeProject();
        }

        this.cd.detectChanges();
    }

    /**
     * Tüm noktaları aktif düzleme project et (yansıt)
     * Her nokta, düzleme en yakın noktaya taşınır
     */
    noktalariDuzlemeProject(): void {
        if (this.noktalar.length === 0) return;

        // Kamera takip modunda project etme (sabit düzlem yok)
        if (this.duzlemKameraTakip) {
            console.log('[Polyline] Kamera takip modunda - noktalar project edilmiyor');
            return;
        }

        // Normal vektörü kontrol et
        const normal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
        if (normal.length() < 0.0001) {
            console.warn('[Polyline] Geçersiz normal vektörü, project yapılamıyor');
            return;
        }
        normal.normalize();

        // THREE.Plane nesnesini güncelle
        const plane = new THREE.Plane(normal, this.duzlemD);

        // Her noktayı düzleme project et
        for (let i = 0; i < this.noktalar.length; i++) {
            const nokta = this.noktalar[i];
            const point3D = new THREE.Vector3(nokta.x, nokta.y, nokta.z);

            // Noktayı düzleme project et
            const projectedPoint = new THREE.Vector3();
            plane.projectPoint(point3D, projectedPoint);

            // Yuvarla ve güncelle
            this.noktalar[i] = {
                x: Math.round(projectedPoint.x * 100) / 100,
                y: Math.round(projectedPoint.y * 100) / 100,
                z: Math.round(projectedPoint.z * 100) / 100
            };
        }

        // Polyline'ı güncelle (3D görünüm)
        this.geciciPolylineGuncelle();
    }

    /**
     * Tek bir noktayı aktif düzleme project et
     * @param point Project edilecek 3D nokta
     * @returns Düzleme project edilmiş nokta
     */
    private noktayiDuzlemeProject(point: THREE.Vector3): THREE.Vector3 {
        // Kamera takip modunda project etme (zaten doğru düzlemde)
        if (this.duzlemKameraTakip) {
            return point;
        }

        // Normal vektörü kontrol et
        const normal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
        if (normal.length() < 0.0001) {
            // Geçersiz normal, noktayı olduğu gibi döndür
            return point;
        }
        normal.normalize();

        // Düzlemi oluştur ve noktayı project et
        const plane = new THREE.Plane(normal, this.duzlemD);
        const projectedPoint = new THREE.Vector3();
        plane.projectPoint(point, projectedPoint);

        return projectedPoint;
    }

    /**
     * Normal vektörü normalize et (birim vektöre dönüştür)
     * NOT: Normalize sadece vektör yönünü değiştirir, düzlem konumunu değiştirmez
     *      bu yüzden noktaları tekrar project etmeye gerek yok
     */
    duzlemNormalize(): void {
        const len = Math.sqrt(
            this.duzlemNormal.x * this.duzlemNormal.x +
            this.duzlemNormal.y * this.duzlemNormal.y +
            this.duzlemNormal.z * this.duzlemNormal.z
        );
        if (len > 0.0001) {
            this.duzlemNormal.x = Math.round((this.duzlemNormal.x / len) * 1000) / 1000;
            this.duzlemNormal.y = Math.round((this.duzlemNormal.y / len) * 1000) / 1000;
            this.duzlemNormal.z = Math.round((this.duzlemNormal.z / len) * 1000) / 1000;
        }
        // Normalize sadece birim vektöre çevirir, düzlemi değiştirmez - project etme
        this.duzlemGuncelle(false);
    }

    /**
     * Seçili yüzeyi temizle ve varsayılana dön (XY düzlemi: z = 0)
     */
    seciliYuzeyTemizle(): void {
        this.seciliYuzey = null;
        this.seciliYuzeyIsmi = '';
        // Kamera takip moduna dön (serbest dolaşım)
        this.duzlemKameraTakip = true;
        // Varsayılan değerleri sıfırla (gösterim için)
        this.duzlemNormal = { x: 0, y: 0, z: 1 };
        this.duzlemD = 0;
        this.cd.detectChanges();
    }

    /**
     * Kamera takip modunu aç (serbest dolaşım)
     */
    duzlemKameraModuAc(): void {
        this.duzlemKameraTakip = true;
        this.seciliYuzey = null;
        this.seciliYuzeyIsmi = '';
        this.cd.detectChanges();
    }

    /**
     * Düzlem seçme modunu başlat (3D'den yüzey/düzlem seçimi)
     */
    duzlemSecBaslat(): void {
        // Zaten seçim modundaysa kapat
        if (this.duzlemSecModu) {
            this.duzlemSecDurdur();
            return;
        }

        // Diğer seçim modlarını kapat
        if (this.noktaSecModu !== null) {
            this.noktaSecDurdur();
        }
        if (this.bodySecModu) {
            this.bodySecDurdur();
        }
        if (this.ardisikTiklamaModu) {
            this.ardisikTiklamaDurdur();
        }

        this.duzlemSecModu = true;

        // Canvas'ı bul
        const canvas = this.canvasBul();
        if (!canvas) {
            this.duzlemSecModu = false;
            console.warn('[Polyline] Düzlem seçimi için canvas bulunamadı');
            return;
        }

        // Click handler - yüzey/düzlem seçimi
        this.duzlemSecClickHandler = (event: MouseEvent) => {
            if (event.button !== 0) return; // Sadece sol tık
            this.duzlemSecCanvasTiklandi(event, canvas);
        };

        // Context menu handler - sağ tık ile iptal
        this.duzlemSecContextMenuHandler = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            this.duzlemSecDurdur();
        };

        // Event listener'ları ekle
        this.ngZone.runOutsideAngular(() => {
            canvas.addEventListener('click', this.duzlemSecClickHandler!);
            canvas.addEventListener('contextmenu', this.duzlemSecContextMenuHandler!);
        });

        this.cd.detectChanges();
        console.log('[Polyline] Düzlem seçme modu başlatıldı - Sağ tık ile iptal');
    }

    /**
     * Düzlem seçme modunu durdur
     */
    duzlemSecDurdur(): void {
        const canvas = this.canvasBul();
        if (canvas) {
            if (this.duzlemSecClickHandler) {
                canvas.removeEventListener('click', this.duzlemSecClickHandler);
            }
            if (this.duzlemSecContextMenuHandler) {
                canvas.removeEventListener('contextmenu', this.duzlemSecContextMenuHandler);
            }
        }

        this.duzlemSecClickHandler = undefined;
        this.duzlemSecContextMenuHandler = undefined;
        this.duzlemSecModu = false;

        this.cd.detectChanges();
        console.log('[Polyline] Düzlem seçme modu durduruldu');
    }

    /**
     * Canvas tıklandığında düzlem/yüzey seç
     */
    private duzlemSecCanvasTiklandi(event: MouseEvent, canvas: HTMLCanvasElement): void {
        const anaGosterim = this.anaGosterim || this.sahne?.anaGosterim;
        if (!anaGosterim?.aktifKamera) return;

        const rect = canvas.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, anaGosterim.aktifKamera);

        // Sahne mesh'lerini topla
        const meshler: THREE.Mesh[] = [];
        const sahne = this.sahne || anaGosterim.sahneler?.[0];
        if (sahne) {
            sahne.traverse((obj: THREE.Object3D) => {
                if ((obj as THREE.Mesh).isMesh) {
                    meshler.push(obj as THREE.Mesh);
                }
            });
        }

        if (meshler.length > 0) {
            const kesisimler = raycaster.intersectObjects(meshler, true);
            if (kesisimler.length > 0) {
                const kesisim = kesisimler[0];
                const mesh = kesisim.object as THREE.Mesh;
                const face = kesisim.face;
                const point = kesisim.point;

                // Yüzey normal'ini al
                if (face) {
                    // Normal'i world space'e çevir
                    const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
                    const worldNormal = face.normal.clone().applyMatrix3(normalMatrix).normalize();

                    // Matematiksel düzlem parametrelerini kaydet
                    // Düzlem denklemi: nx*x + ny*y + nz*z + d = 0
                    // d = -(n · P) burada P, düzlem üzerindeki bir nokta
                    this.duzlemNormal = {
                        x: Math.round(worldNormal.x * 1000) / 1000,
                        y: Math.round(worldNormal.y * 1000) / 1000,
                        z: Math.round(worldNormal.z * 1000) / 1000
                    };
                    this.duzlemD = Math.round(-worldNormal.dot(point) * 100) / 100;

                    // THREE.Plane nesnesini güncelle
                    this._duzlemPlane.setFromNormalAndCoplanarPoint(worldNormal, point);

                    // Yüzey ismini belirle
                    const meshIsim = mesh.name || mesh.parent?.name || 'Yüzey';
                    this.seciliYuzey = mesh;
                    this.seciliYuzeyIsmi = meshIsim;

                    // Kamera takip modunu kapat (sabit düzlem)
                    this.duzlemKameraTakip = false;

                    console.log('[Polyline] Yüzey seçildi:', meshIsim);
                    console.log('[Polyline] Düzlem: ', this.duzlemNormal.x, 'x +', this.duzlemNormal.y, 'y +', this.duzlemNormal.z, 'z +', this.duzlemD, '= 0');

                    // Mevcut noktaları yeni düzleme project et
                    this.noktalariDuzlemeProject();
                }

                // Seçme modunu kapat
                this.duzlemSecDurdur();
            }
        }
    }

    // ========================================================================
    // ARDIŞIK TIKLAMA MODU
    // ========================================================================

    /**
     * Ardışık tıklama modunu başlat
     */
    ardisikTiklamaBaslat(): void {
        if (this.ardisikTiklamaModu) return; // Zaten aktif

        const canvas = this.canvasBul();
        if (!canvas) {
            console.warn('[Polyline] Ardışık tıklama için canvas bulunamadı');
            return;
        }

        this.ardisikTiklamaModu = true;
        this.cd.detectChanges();

        // Click handler oluştur - NgZone dışında çalışacak
        this.ardisikTiklamaHandler = (event: MouseEvent) => {
            // Sol tık kontrolü
            if (event.button !== 0) return;

            // Nokta hesapla ve ekle
            let hitPoint = this.raycastNoktaHesapla(event, canvas);
            if (!hitPoint) {
                // Model üzerine tıklanmadıysa düzlemde hesapla
                hitPoint = this.duzlemNoktaHesapla(event, canvas);
            } else {
                // Model üzerine tıklandıysa, seçili düzleme project et
                hitPoint = this.noktayiDuzlemeProject(hitPoint);
            }
            if (!hitPoint) return;

            const roundedX = Math.round(hitPoint.x * 100) / 100;
            const roundedY = Math.round(hitPoint.y * 100) / 100;
            const roundedZ = Math.round(hitPoint.z * 100) / 100;

            // Change detection'ı devre dışı bırak, güncelle, sonra tekrar etkinleştir
            this.cd.detach();

            // Yeni nokta ekle
            this.noktalar.push({ x: roundedX, y: roundedY, z: roundedZ });
            this.seciliNoktaIndex = this.noktalar.length - 1;

            // Polyline'ı güncelle (3D görünüm)
            this.geciciPolylineGuncelle();

            // requestAnimationFrame ile UI güncelle (NG0100 fix)
            requestAnimationFrame(() => {
                this.cd.reattach();
                this.cd.detectChanges();
            });
        };

        // Sağ tık ile bitirme handler'ı
        this.ardisikContextMenuHandler = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            // Change detection'ı kontrollü yap
            this.cd.detach();
            this.ardisikTiklamaDurdurInternal();
            requestAnimationFrame(() => {
                this.cd.reattach();
                this.cd.detectChanges();
            });
        };

        // Event listener'ları zone dışında ekle (performans için)
        this.ngZone.runOutsideAngular(() => {
            canvas.addEventListener('click', this.ardisikTiklamaHandler!);
            canvas.addEventListener('contextmenu', this.ardisikContextMenuHandler!);
        });
        console.log('[Polyline] Ardışık tıklama modu başlatıldı - Sağ tık ile bitir');
    }

    /**
     * Ardışık tıklama modunu durdur (internal - detectChanges çağırmaz)
     */
    private ardisikTiklamaDurdurInternal(): void {
        const canvas = this.canvasBul();
        if (canvas) {
            if (this.ardisikTiklamaHandler) {
                canvas.removeEventListener('click', this.ardisikTiklamaHandler);
            }
            if (this.ardisikContextMenuHandler) {
                canvas.removeEventListener('contextmenu', this.ardisikContextMenuHandler);
            }
        }

        this.ardisikTiklamaHandler = undefined;
        this.ardisikContextMenuHandler = undefined;
        this.ardisikTiklamaModu = false;
        console.log('[Polyline] Ardışık tıklama modu durduruldu');
    }

    /**
     * Ardışık tıklama modunu durdur
     */
    ardisikTiklamaDurdur(): void {
        if (!this.ardisikTiklamaModu) return;

        this.ardisikTiklamaDurdurInternal();

        // Change detection tetikle
        this.cd.detectChanges();
    }

    /**
     * Ardışık tıklama modunu toggle et
     */
    ardisikTiklamaToggle(): void {
        if (this.ardisikTiklamaModu) {
            this.ardisikTiklamaDurdur();
        } else {
            this.ardisikTiklamaBaslat();
        }
    }

    // ========================================================================
    // HANDLE SİSTEMİ
    // ========================================================================

    /**
     * Handle'ları başlat (retry mekanizmalı - Line dialog pattern)
     */
    private handleBaslat(): void {
        const hedefPolyline = this.geciciPolylineAktif ? this.geciciPolyline : this._mevcutPolyline;
        if (!hedefPolyline) {
            console.warn('[Polyline Handle] Hedef polyline yok, handle başlatılamıyor');
            return;
        }

        // Önce mevcut handle'ları temizle
        this.handleDurdur();

        // Sahneyi al - öncelik sırası (Line dialog pattern)
        let sahneObj = (hedefPolyline as any)?.sahne || this.sahne || this.anaGosterim?.sahneler?.[0];

        // Sahne bulunamadıysa parent'ı traverse et
        if (!sahneObj && hedefPolyline?.parent) {
            let parent = hedefPolyline.parent;
            while (parent && parent.type !== 'Scene') {
                parent = parent.parent as any;
            }
            if (parent) sahneObj = parent;
        }

        if (!sahneObj) {
            // Sahne henüz hazır değil, retry yap
            if (this.handleBaslatRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleBaslatRetryCount++;
                console.warn(`[Polyline Handle] Sahne hazır değil, retry ${this.handleBaslatRetryCount}/${this.HANDLE_MAX_RETRY}`);
                this.handleBaslatTimeoutId = setTimeout(() => this.handleBaslat(), this.HANDLE_RETRY_DELAY);
            } else {
                console.error('[Polyline Handle] Sahne bulunamadı, maksimum retry aşıldı');
                this.handleBaslatRetryCount = 0;
            }
            return;
        }

        // Canvas kontrolü
        const canvas = this.canvasBul();
        if (!canvas) {
            if (this.handleBaslatRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleBaslatRetryCount++;
                console.warn(`[Polyline Handle] Canvas hazır değil, retry ${this.handleBaslatRetryCount}/${this.HANDLE_MAX_RETRY}`);
                this.handleBaslatTimeoutId = setTimeout(() => this.handleBaslat(), this.HANDLE_RETRY_DELAY);
            } else {
                console.error('[Polyline Handle] Canvas bulunamadı, maksimum retry aşıldı');
                this.handleBaslatRetryCount = 0;
            }
            return;
        }

        // Retry başarılı, sayacı sıfırla
        this.handleBaslatRetryCount = 0;

        // Orphan handle'ları temizle
        this.orphanHandleTemizle();

        // Handle'ları göster
        this.handles = hedefPolyline.handlelerGoster();

        // Handle görünürlük kontrolü
        if (this.handles) {
            this.handles.visible = true;
            this.handles.traverse((child: THREE.Object3D) => {
                child.visible = true;
                child.frustumCulled = false;
            });
            this.handles.guncelle();
        }

        this.handleAktif = true;

        // Event listener'ları ekle
        this.handleEventleriEkle();

        // Render tetikle
        if (this.anaGosterim?.sunucu && this.anaGosterim?.aktifKamera) {
            this.anaGosterim.sunucu.render(sahneObj, this.anaGosterim.aktifKamera);
        }

        console.log('[Polyline Handle] Handle\'lar başarıyla başlatıldı');
    }

    /**
     * Handle'ları durdur
     */
    private handleDurdur(): void {
        // Pending timeout'u iptal et
        if (this.handleBaslatTimeoutId) {
            clearTimeout(this.handleBaslatTimeoutId);
            this.handleBaslatTimeoutId = undefined;
        }

        // Retry sayacını sıfırla
        this.handleBaslatRetryCount = 0;

        this.handleAktif = false;

        // Event listener'ları kaldır
        this.handleEventleriKaldir();

        // Handle'ları gizle
        const hedefPolyline = this.geciciPolylineAktif ? this.geciciPolyline : this._mevcutPolyline;
        if (hedefPolyline) {
            hedefPolyline.handlelerGizle();
        }
        this.handles = undefined;
    }

    /**
     * Orphan handle'ları temizle
     */
    private orphanHandleTemizle(): void {
        const hedefPolyline = this.geciciPolylineAktif ? this.geciciPolyline : this._mevcutPolyline;
        if (!hedefPolyline || !this.sahne) return;

        const buPolylineHandleIsmi = 'PolylineHandles_' + hedefPolyline.uuid;
        const eskiHandles: THREE.Object3D[] = [];

        this.sahne.traverse(child => {
            if (child.name === buPolylineHandleIsmi && child !== this.handles) {
                eskiHandles.push(child);
            }
        });

        eskiHandles.forEach(h => {
            if (h.parent) h.parent.remove(h);
        });
    }

    /**
     * Handle eventlerini ekle
     */
    private handleEventleriEkle(): void {
        const canvas = this.canvasBul();
        if (!canvas) return;

        this.handleMouseDownRef = (e: MouseEvent) => this.handleMouseDown(e);
        this.handleMouseMoveRef = (e: MouseEvent) => this.handleMouseMove(e);
        this.handleMouseUpRef = (e: MouseEvent) => this.handleMouseUp(e);

        canvas.addEventListener('mousedown', this.handleMouseDownRef);
        canvas.addEventListener('mousemove', this.handleMouseMoveRef);
        canvas.addEventListener('mouseup', this.handleMouseUpRef);
    }

    /**
     * Handle eventlerini kaldır
     */
    private handleEventleriKaldir(): void {
        const canvas = this.canvasBul();
        if (!canvas) return;

        if (this.handleMouseDownRef) {
            canvas.removeEventListener('mousedown', this.handleMouseDownRef);
        }
        if (this.handleMouseMoveRef) {
            canvas.removeEventListener('mousemove', this.handleMouseMoveRef);
        }
        if (this.handleMouseUpRef) {
            canvas.removeEventListener('mouseup', this.handleMouseUpRef);
        }
    }

    /**
     * Canvas bul (FikoSecililer pattern ile aynı)
     */
    private canvasBul(): HTMLCanvasElement | null {
        // 1. FikoSecililer pattern: anaGosterim.canvas.nativeElement
        if (this.anaGosterim?.canvas?.nativeElement) {
            return this.anaGosterim.canvas.nativeElement as HTMLCanvasElement;
        }

        // 2. Alternatif: sunucu.domElement (WebGLRenderer)
        if (this.anaGosterim?.sunucu?.domElement) {
            return this.anaGosterim.sunucu.domElement as HTMLCanvasElement;
        }

        // 3. renderer.domElement (eski pattern)
        if (this.anaGosterim?.renderer?.domElement) {
            return this.anaGosterim.renderer.domElement as HTMLCanvasElement;
        }

        // 4. sahne üzerinden dene
        if (this.sahne) {
            const sahnedekiAnaGosterim = (this.sahne as any).anaGosterim;
            if (sahnedekiAnaGosterim?.canvas?.nativeElement) {
                return sahnedekiAnaGosterim.canvas.nativeElement as HTMLCanvasElement;
            }
            if (sahnedekiAnaGosterim?.sunucu?.domElement) {
                return sahnedekiAnaGosterim.sunucu.domElement as HTMLCanvasElement;
            }
        }

        // 5. DOM'dan direkt arama yap (fallback)
        const canvasElement = document.querySelector('canvas#fikoThreeCanvas') ||
                             document.querySelector('canvas.fiko-threejs-canvas') ||
                             document.querySelector('.fiko-three-js-container canvas') ||
                             document.querySelector('fikoyt-3d canvas') ||
                             document.querySelector('canvas');
        if (canvasElement) return canvasElement as HTMLCanvasElement;

        return null;
    }

    /**
     * Handle mouse down
     */
    private handleMouseDown(event: MouseEvent): void {
        if (!this.handles || !this.handleAktif) return;

        const hedefPolyline = this.geciciPolylineAktif ? this.geciciPolyline : this._mevcutPolyline;
        if (!hedefPolyline) return;

        const canvas = this.canvasBul();
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const camera = this.anaGosterim?.aktifKamera || this.sahne?.anaGosterim?.aktifKamera;
        if (!camera) return;

        this.handles.updateMatrixWorld(true);

        this.handleRaycaster.setFromCamera(new THREE.Vector2(x, y), camera);
        this.handleRaycaster.near = 0.001;
        this.handleRaycaster.far = 1000000;

        const handleMeshes = this.handles.getRaycastTargets();
        const intersects = this.handleRaycaster.intersectObjects(handleMeshes, true);

        let handleIndex: number | null = null;

        if (intersects.length > 0) {
            const hitHandle = intersects[0].object;
            handleIndex = hitHandle.userData['handleIndex'] ?? hitHandle.parent?.userData['handleIndex'] ?? null;
        } else {
            // Manuel küre kesişim testi (fallback)
            const manuelSonuc = this.handles.manuelKureKesisim(this.handleRaycaster.ray);
            if (manuelSonuc.hit) {
                handleIndex = manuelSonuc.handleIndex;
            }
        }

        if (handleIndex !== null) {
            this.handles.isDragging = true;
            this.handles.dragTarget = handleIndex;
            this.seciliNoktaIndex = handleIndex;

            const noktaPoz = hedefPolyline.noktalar[handleIndex];

            // Kamera takip modunda veya sabit düzlem modunda sürükleme düzlemini ayarla
            if (this.duzlemKameraTakip) {
                // Kamera takip modu - kamera yönüne göre düzlem
                const cameraDir = new THREE.Vector3();
                camera.getWorldDirection(cameraDir);
                this.dragPlane.setFromNormalAndCoplanarPoint(cameraDir.clone().negate(), noktaPoz);
            } else {
                // Sabit düzlem modu - seçili/ayarlanmış düzlemi kullan
                const duzlemNormal = new THREE.Vector3(this.duzlemNormal.x, this.duzlemNormal.y, this.duzlemNormal.z);
                if (duzlemNormal.length() > 0.0001) {
                    duzlemNormal.normalize();
                    this.dragPlane.set(duzlemNormal, this.duzlemD);
                } else {
                    // Normal geçersizse kamera yönünü kullan
                    const cameraDir = new THREE.Vector3();
                    camera.getWorldDirection(cameraDir);
                    this.dragPlane.setFromNormalAndCoplanarPoint(cameraDir.clone().negate(), noktaPoz);
                }
            }

            const startIntersect = new THREE.Vector3();
            if (this.handleRaycaster.ray.intersectPlane(this.dragPlane, startIntersect)) {
                this.dragOffset.copy(startIntersect).sub(noktaPoz);
            } else {
                this.dragOffset.set(0, 0, 0);
            }

            const kontrol = this.anaGosterim?.aktifKontrol || this.sahne?.anaGosterim?.aktifKontrol;
            if (kontrol) {
                kontrol.enabled = false;
            }

            canvas.style.cursor = 'grabbing';
            event.preventDefault();
            event.stopPropagation();

            this.cd.detectChanges();
        }
    }

    /**
     * Handle mouse move
     */
    private handleMouseMove(event: MouseEvent): void {
        if (!this.handles || !this.handleAktif) return;

        const canvas = this.canvasBul();
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const camera = this.anaGosterim?.aktifKamera || this.sahne?.anaGosterim?.aktifKamera;
        if (!camera) return;

        // Sürükleme modunda değilse hover
        if (!this.handles.isDragging) {
            this.handles.updateMatrixWorld(true);
            this.handleRaycaster.setFromCamera(new THREE.Vector2(x, y), camera);
            this.handleRaycaster.near = 0.001;
            this.handleRaycaster.far = 1000000;

            const handleMeshes = this.handles.getRaycastTargets();
            const intersects = this.handleRaycaster.intersectObjects(handleMeshes, true);

            let hover = false;
            if (intersects.length > 0) {
                this.handles.setHover(intersects[0].object);
                hover = true;
            } else {
                const manuelSonuc = this.handles.manuelKureKesisim(this.handleRaycaster.ray);
                if (manuelSonuc.hit && manuelSonuc.handleIndex !== null) {
                    this.handles.setHover(this.handles.handleler[manuelSonuc.handleIndex]);
                    hover = true;
                } else {
                    this.handles.setHover(null);
                }
            }

            canvas.style.cursor = hover ? 'grab' : 'default';
            return;
        }

        // Sürükleme modunda
        const hedefPolyline = this.geciciPolylineAktif ? this.geciciPolyline : this._mevcutPolyline;
        if (!hedefPolyline) return;

        this.handleRaycaster.setFromCamera(new THREE.Vector2(x, y), camera);
        this.handleRaycaster.near = 0.001;
        this.handleRaycaster.far = 1000000;

        const intersectPoint = new THREE.Vector3();
        if (this.handleRaycaster.ray.intersectPlane(this.dragPlane, intersectPoint)) {
            const yeniPozisyon = intersectPoint.sub(this.dragOffset);

            // Polyline noktasını güncelle
            const index = this.handles.dragTarget!;
            hedefPolyline.noktaGuncelle(index, yeniPozisyon);

            // Form değerlerini güncelle
            this.noktalar[index] = {
                x: Math.round(yeniPozisyon.x * 100) / 100,
                y: Math.round(yeniPozisyon.y * 100) / 100,
                z: Math.round(yeniPozisyon.z * 100) / 100
            };

            // Handle'ları güncelle
            this.handles.guncelle();

            this.cd.detectChanges();
        }
    }

    /**
     * Handle mouse up
     */
    private handleMouseUp(event: MouseEvent): void {
        if (!this.handles) return;

        if (this.handles.isDragging) {
            this.handles.isDragging = false;
            this.handles.dragTarget = null;

            const kontrol = this.anaGosterim?.aktifKontrol || this.sahne?.anaGosterim?.aktifKontrol;
            if (kontrol) {
                kontrol.enabled = true;
            }

            const canvas = this.canvasBul();
            if (canvas) {
                canvas.style.cursor = 'default';
            }
        }
    }

    // ========================================================================
    // DEĞER DEĞİŞİKLİKLERİ
    // ========================================================================

    kapaliDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    renkDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    kalinlikDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    opaklikDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    stilDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    cizgiTipiDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    noktaGosterDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    enUsteDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    renderEtDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    secilebilirDegisti(): void {
        this.geciciPolylineGuncelle();
    }

    sekilParametresiDegisti(): void {
        // Sonsuz döngüyü önle - şekil oluşturulurken UI güncellemesi yapılıyorsa çık
        if (this._sekilOlusturuluyor) return;

        // Otomatik modda yarıçap değiştiğinde nokta sayısını yeniden hesapla
        if (this.otomatikNoktaSayisi && this.hazirSekil === 'daire') {
            this.daireNoktaSayisi = this.toleransIleNoktaSayisiHesapla(this.sekilYaricap, this.tolerans);
        }

        if (this.hazirSekil !== 'serbest') {
            this.hazirSekilOlustur();
        }
    }

    // ========================================================================
    // OLUŞTUR / GÜNCELLE
    // ========================================================================

    /**
     * Polyline oluştur
     */
    olustur(): void {
        // Sahne referansını al (fallback: anaGosterim.sahneler[0])
        const hedefSahne = this.sahne || this.anaGosterim?.sahneler?.[0];
        if (!hedefSahne) {
            console.warn('[FikoYT3dPolyline] Sahne bulunamadı, polyline oluşturulamıyor');
            return;
        }

        // Geçici polyline'ı kalıcı yap
        if (this.geciciPolyline) {
            const polyline = this.geciciPolyline;

            // İsim ver
            polyline.name = this.isim || `Polyline.${FikoYT3dPolyline.SAYAC}`;
            polyline.etiket = polyline.name;

            // Handle'ları gizle
            polyline.handlelerGizle();

            // Aktif body'ye ekle (ürün ağacında görünmesi için)
            if (this.aktifBodyeEkle && this.seciliAktifBody) {
                this.polylineyiBodyeEkle(polyline, this.seciliAktifBody);
            }

            // Emit
            this.polylineOlusturuldu.emit(polyline);

            // Geçici referansı temizle (sahneye eklendi zaten)
            this.geciciPolyline = undefined;
            this.geciciPolylineAktif = false;

            // Dialog'u kapat
            this.kapat();
        }
    }

    /**
     * Polyline'ı aktif body'ye ekle ve ürün ağacını güncelle
     */
    private polylineyiBodyeEkle(polyline: FikoYT3dPolyline, body: any): void {
        if (!polyline || !body) return;

        try {
            // Sahne referansını ayarla
            const sahneRef = body.sahne || this.sahne || this.anaGosterim?.sahneler?.[0];
            if (sahneRef) {
                (polyline as any).sahne = sahneRef;
            }

            // userData.FikoArayuz'u ayarla (ürün ağacında görünmesi için)
            if (!polyline.userData) polyline.userData = {};
            polyline.userData.FikoArayuz = {
                agactaGorunur: true,
                listeGoster: false,
                simge: '/simgeler/polyline.png'
            };

            // Body bir THREE.Object3D ise doğrudan ekle
            if (body.add && typeof body.add === 'function') {
                body.add(polyline);
            }

            // Body'nin liste array'ine de ekle (ürün ağacı navigasyonu için)
            try {
                if (body._liste && Array.isArray(body._liste)) {
                    if (!body._liste.includes(polyline)) {
                        body._liste.push(polyline);
                    }
                } else if (body.liste && Array.isArray(body.liste)) {
                    if (!body.liste.includes(polyline)) {
                        body.liste.push(polyline);
                    }
                }
            } catch {
                // Liste ekleme hatası - görmezden gel
            }

            // FikoYT.degisti emit et - ürün ağacının güncellenmesi için
            this.fikoYT.degisti.emit();

            // Change detection tetikle
            this.cd.detectChanges();
        } catch {
            // Body'ye ekleme hatası - görmezden gel
        }
    }

    /**
     * Mevcut polyline'ı güncelle (düzenleme modu)
     */
    guncelle(): void {
        if (!this._mevcutPolyline) return;

        // Noktaları güncelle
        const noktalarVec = this.noktalar.map(n => new THREE.Vector3(n.x, n.y, n.z));
        this._mevcutPolyline.noktalarileBelirle(noktalarVec, this.kapali);

        // İsim güncelle
        if (this.isim) {
            this._mevcutPolyline.name = this.isim;
            this._mevcutPolyline.etiket = this.isim;
        }

        // Ayarları uygula
        this.ayarlariUygula(this._mevcutPolyline);

        // Dialog'u kapat
        this.kapat();
    }

    /**
     * Sıfırla
     */
    sifirla(): void {
        this.formDegerleriniSifirla();
        this.geciciPolylineGuncelle();
    }

    /**
     * Toplam uzunluk
     */
    get toplamUzunluk(): number {
        let uzunluk = 0;
        for (let i = 0; i < this.noktalar.length - 1; i++) {
            const p1 = this.noktalar[i];
            const p2 = this.noktalar[i + 1];
            uzunluk += Math.sqrt(
                Math.pow(p2.x - p1.x, 2) +
                Math.pow(p2.y - p1.y, 2) +
                Math.pow(p2.z - p1.z, 2)
            );
        }
        if (this.kapali && this.noktalar.length > 2) {
            const p1 = this.noktalar[this.noktalar.length - 1];
            const p2 = this.noktalar[0];
            uzunluk += Math.sqrt(
                Math.pow(p2.x - p1.x, 2) +
                Math.pow(p2.y - p1.y, 2) +
                Math.pow(p2.z - p1.z, 2)
            );
        }
        return Math.round(uzunluk * 100) / 100;
    }
}
